import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Camera, Check, FileText, Plus, RefreshCw, Search, Truck, X } from 'lucide-react';
import { api, errorMessage } from '../lib/api';
import { loadOrdersCache, rememberSession, saveOrdersCache } from '../lib/store';
import { clearReceipt, syncReceipt } from '../lib/sync';
import { combineDestinations, combineOrders, fetchOrders, loadCombinedProgress, saveCombinedProgress } from '../lib/combine';
import { ScanSink, useScanInput } from '../lib/useScanInput';
import { liveDest, resolveBarcode, unitVerdict } from '../lib/verdict';
import { boxLabel, cartonsScanned, isMultiBox, lineMatches, missingBoxes, nextScanCount, pickScanTarget, scanChoices, unitsFromBoxes, withLinkedCode } from '../lib/boxes';
import { cn, fmtDate, fmtVolume, fmtWeight, qty, vibrate } from '../lib/utils';
import TopBar from '../components/TopBar';
import LineRow from '../components/LineRow';
import VerdictSheet from '../components/VerdictSheet';
import QtySheet from '../components/QtySheet';
import UnknownSheet from '../components/UnknownSheet';
import ConfirmSheet from '../components/ConfirmSheet';
import BoxSheet from '../components/BoxSheet';
import ChooseLineSheet from '../components/ChooseLineSheet';
import PickOrdersSheet from '../components/PickOrdersSheet';
import { OrderTags } from '../components/Tags';
import Loader from '../components/Loader';

const CameraScanner = lazy(() => import('../components/CameraScanner'));

const LINE_PAGE = 80; // a 505-line order: the rest on request
const boxesOf = (progress, lineId) => (progress.boxes && progress.boxes[lineId]) || {};

/* One purchase order (/orders/:id) or several received together
   (/receive/:ids). Everything below works on the combined order. */
export default function Receive() {
  const { id, ids } = useParams();
  const key = ids || id || '';
  const poIds = useMemo(() => key.split(',').map(Number).filter(Boolean), [key]);
  const navigate = useNavigate();
  const location = useLocation();

  const [order, setOrder] = useState(null);
  const [dest, setDest] = useState(undefined); // undefined = computing, null = failed
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(() => loadCombinedProgress(poIds));
  const [verdict, setVerdict] = useState(null);
  const [openLineId, setOpenLineId] = useState(null);
  const [unknown, setUnknown] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [camera, setCamera] = useState(false);
  const [boxSheet, setBoxSheet] = useState(null); // { line, code, box, reopen }
  const [choice, setChoice] = useState(null); // { code, choices }: a scanned EAN shared by several products
  const [adding, setAdding] = useState(null); // open orders to pick from, to add a forgotten one
  const lastChoiceRef = useRef({}); // code -> line id chosen last time, listed first next time
  const [flash, setFlash] = useState(0);
  const [showDone, setShowDone] = useState(false);
  const [lineLimit, setLineLimit] = useState(LINE_PAGE);
  const [lastLine, setLastLine] = useState(null); // shown even past the limit, the screen follows it
  const [find, setFind] = useState(''); // filter the lines by vendor code, EAN or name; a scan into the field finds too

  const orderRef = useRef(order);
  const destRef = useRef(dest);
  const progressRef = useRef(progress);
  const promptedRef = useRef(new Set()); // lines already asked to confirm their packaging this session
  orderRef.current = order;
  destRef.current = dest;
  progressRef.current = progress;

  const load = useCallback(
    (refresh = false) => {
      setError(null);
      if (refresh) setDest(undefined);
      fetchOrders(poIds, api.order)
        .then((orders) => setOrder(combineOrders(orders)))
        .catch((err) => setError(errorMessage(err, 'Could not load this order')));
      // One classification for the whole session: a customer order whose other
      // line arrives on another PO of this truck is OUT, not CROSSDOCK.
      api
        .destinations(poIds[0], refresh, poIds.slice(1))
        .then((d) => setDest(combineDestinations([d])))
        .catch(() => setDest(null));
    },
    [poIds]
  );

  useEffect(() => {
    setOrder(null);
    setProgress(loadCombinedProgress(poIds));
    setVerdict(null);
    promptedRef.current = new Set();
    load(false);
  }, [poIds, load]);

  /* The receipt follows the login: what this device counted goes to the
     server shortly after, and what other devices of the login counted comes
     back. Scanning never waits for it. */
  const syncState = useRef({ busy: false, again: false, timer: null });
  const keyRef = useRef(key);
  keyRef.current = key;
  const sync = useCallback(async () => {
    const st = syncState.current;
    const o = orderRef.current;
    if (!o || !poIds.every((p) => o.ids.includes(p))) return;
    if (st.busy) {
      st.again = true;
      return;
    }
    st.busy = true;
    const forKey = keyRef.current;
    try {
      const finish = await syncReceipt(o, progressRef.current);
      if (finish && keyRef.current === forKey) setProgress((p) => finish(p));
    } finally {
      st.busy = false;
      if (st.again) {
        st.again = false;
        sync();
      }
    }
  }, [poIds]);
  const syncSoon = useCallback(() => {
    const st = syncState.current;
    clearTimeout(st.timer);
    st.timer = setTimeout(sync, 1500);
  }, [sync]);

  const updateProgress = useCallback(
    (fn) => {
      setProgress((p) => {
        const next = fn(p);
        if (orderRef.current) saveCombinedProgress(orderRef.current, next);
        return next;
      });
      syncSoon();
    },
    [syncSoon]
  );

  // pick up the other devices' counts: when the order is in, every 15 s on screen, and on coming back to the app
  const orderKey = order?.ids?.join(',') || '';
  useEffect(() => {
    if (!orderKey) return undefined;
    const st = syncState.current;
    sync();
    const t = setInterval(() => document.visibilityState === 'visible' && sync(), 15000);
    const onVisible = () => document.visibilityState === 'visible' && sync();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      clearTimeout(st.timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [orderKey, sync]);

  /* One scanned carton. `entry` is the barcode-map hit ({line_id, kind, box_id?}). */
  const countLine = useCallback(
    (line, code, entry, extra = {}) => {
      const p = progressRef.current;
      const multi = isMultiBox(line);
      const boxId = entry?.box_id || (line.boxes.length === 1 ? line.boxes[0].id : null);
      const nextBoxes = { ...boxesOf(p, line.id) };
      let n;
      let units;
      let boxIndex = -1;
      let fullSet = false;
      if (multi) {
        if (boxId) {
          nextBoxes[boxId] = Number(nextBoxes[boxId] || 0) + 1;
          n = nextBoxes[boxId];
          boxIndex = line.boxes.findIndex((b) => b.id === boxId);
        } else {
          const base = Math.min(...line.boxes.map((b) => Number(nextBoxes[b.id] || 0)));
          line.boxes.forEach((b) => {
            nextBoxes[b.id] = Math.max(Number(nextBoxes[b.id] || 0), base + 1);
          });
          n = base + 1;
          fullSet = true;
        }
        units = unitsFromBoxes(line, nextBoxes);
      } else {
        n = Number(p.counts[line.id] || 0) + 1;
        units = n;
        if (boxId) nextBoxes[boxId] = n;
      }
      // Judge with this carton counted: a dependency on itself is met once it is scanned.
      const nextCounts = { ...p.counts, [line.id]: units };
      const d = liveDest(destRef.current?.lines?.[line.id], nextCounts);
      const kind = n > line.to_receive ? 'over' : unitVerdict(d, n);
      updateProgress((prev) => ({
        ...prev,
        counts: { ...prev.counts, [line.id]: units },
        boxes: { ...(prev.boxes || {}), [line.id]: nextBoxes },
        scans: [...prev.scans, { barcode: code, line_id: line.id, box_id: boxId, matched: true, destination: kind, at: new Date().toISOString() }].slice(-1000),
        startedAt: prev.startedAt || new Date().toISOString(),
      }));
      setVerdict({
        ...extra,
        line,
        code,
        n,
        units,
        kind,
        dest: d,
        fullSet,
        box: multi && boxIndex >= 0 ? { index: boxIndex, total: line.boxes.length, label: boxLabel(line.boxes[boxIndex], boxIndex) } : null,
        missing: multi && boxIndex >= 0 ? missingBoxes(line, nextBoxes, n).map((b) => boxLabel(b, line.boxes.indexOf(b))) : [],
      });
      setFlash((f) => f + 1);
      setLastLine(line.id);
      // still more of this line to come: keep it in view (a finished one sinks to the bottom)
      if (units < line.to_receive) requestAnimationFrame(() => document.getElementById(`line-${line.id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
      vibrate(kind === 'over' ? [70, 50, 70] : 40);
      // A vendor EAN on file lets the carton scan, but Odoo will refuse the
      // receipt while the packaging is not confirmed. Say so now, once per
      // line, with the packaging panel ready; the confirm sheet blocks again
      // if it stays unconfirmed.
      if (line.status === 'open' && line.packaging_status !== 'confirmed' && !promptedRef.current.has(line.id)) {
        promptedRef.current.add(line.id);
        setOpenLineId(line.id);
      }
    },
    [updateProgress]
  );

  const handleScan = useCallback(
    (raw) => {
      const o = orderRef.current;
      if (!o) return;
      setOpenLineId(null); // the next carton: back to the receipt, where its verdict shows
      const { code, entries } = resolveBarcode(o.barcodes, raw);
      if (!entries) {
        updateProgress((prev) => ({
          ...prev,
          scans: [...prev.scans, { barcode: code, line_id: null, matched: false, at: new Date().toISOString() }].slice(-1000),
        }));
        setVerdict(null);
        setUnknown({ code });
        vibrate([60, 40, 60]);
        return;
      }
      // An EAN shared by different products: the operator chooses. The same
      // product on several orders is settled by the oldest-order rule.
      const choices = scanChoices(entries, o.lines, progressRef.current);
      if (choices) {
        setVerdict(null);
        setChoice({ code, choices });
        vibrate([40, 40, 40]);
        return;
      }
      const pick = pickScanTarget(entries, o.lines, progressRef.current);
      if (!pick) return;
      // Every line this code belongs to is complete: a scan never counts an
      // extra (the floor asked). Say so, and offer the other open orders
      // that still expect this product; + in the line sheet still can.
      if (nextScanCount(pick.line, pick.entry, progressRef.current) > pick.line.to_receive) {
        updateProgress((prev) => ({
          ...prev,
          scans: [...prev.scans, { barcode: code, line_id: pick.line.id, box_id: pick.entry?.box_id || null, matched: true, destination: 'refused_extra', at: new Date().toISOString() }].slice(-1000),
        }));
        setVerdict(null);
        setUnknown({ code, full: { line: pick.line, entry: pick.entry } });
        vibrate([60, 40, 60]);
        return;
      }
      countLine(pick.line, code, pick.entry, { alsoOn: pick.alsoOn });
    },
    [countLine, updateProgress]
  );

  const modalOpen = Boolean(openLineId || unknown || confirming || camera || boxSheet || choice || adding);
  // a scan with only the line sheet open closes it and counts; any other sheet drops the scan
  const scanning = Boolean(order) && !(unknown || confirming || camera || boxSheet || choice || adding);
  useScanInput(handleScan, { enabled: scanning });

  // Arrived here from the order list: a scanned carton is counted straight
  // away, a product found by code opens its line.
  const initialScan = location.state?.scan;
  const initialLine = location.state?.open_line;
  const handledKey = useRef(null); // one carried scan per navigation, also when orders are added to a session
  useEffect(() => {
    if ((!initialScan && !initialLine) || handledKey.current === location.key) return;
    // wait for this route's orders: right after adding one to a session the previous set is still on screen
    if (!order || !poIds.every((id) => order.ids?.includes(id))) return;
    handledKey.current = location.key;
    navigate(location.pathname, { replace: true, state: null });
    if (initialScan) handleScan(initialScan);
    else if (order.lines.some((l) => l.id === initialLine)) setOpenLineId(initialLine);
  }, [order, poIds, initialScan, initialLine, handleScan, navigate, location.pathname, location.key]);

  const openLines = useMemo(() => (order ? order.lines.filter((l) => l.status === 'open') : []), [order]);
  const finding = find.trim().length > 0;
  const shownOpen = useMemo(() => {
    if (finding) return openLines.filter((l) => lineMatches(l, find));
    const left = (l) => Number(progress.counts[l.id] || 0) < l.to_receive;
    return [...openLines.filter(left), ...openLines.filter((l) => !left(l))]; // what is still to count comes first
  }, [openLines, find, finding, progress.counts]);
  const visibleOpen = finding ? shownOpen : shownOpen.filter((l, i) => i < lineLimit || l.id === lastLine);
  const doneLines = useMemo(() => (order ? order.lines.filter((l) => l.status !== 'open') : []), [order]);
  const shownDone = useMemo(() => (finding ? doneLines.filter((l) => lineMatches(l, find)) : showDone ? doneLines : []), [doneLines, find, finding, showDone]);
  const counted = useMemo(() => openLines.reduce((s, l) => s + Number(progress.counts[l.id] || 0), 0), [openLines, progress]);
  const cartons = useMemo(() => openLines.reduce((s, l) => s + cartonsScanned(l, progress.counts[l.id], boxesOf(progress, l.id)), 0), [openLines, progress]);
  const expected = order?.totals?.to_receive || 0;
  const expectedCartons = order?.totals?.cartons || 0;
  const pct = expectedCartons ? Math.min(100, (cartons / expectedCartons) * 100) : 0;
  const openLine = openLineId ? order?.lines.find((l) => l.id === openLineId) : null;
  const destFor = (lineId) => (dest === undefined ? undefined : liveDest(dest?.lines?.[lineId] || null, progress.counts));

  const setUnits = (line, n) => {
    updateProgress((prev) => ({
      ...prev,
      counts: { ...prev.counts, [line.id]: n },
      boxes: { ...(prev.boxes || {}), [line.id]: Object.fromEntries(line.boxes.map((b) => [b.id, n])) },
      startedAt: prev.startedAt || new Date().toISOString(),
    }));
  };
  const setBox = (line, boxId, n) => {
    updateProgress((prev) => {
      const nextBoxes = { ...boxesOf(prev, line.id), [boxId]: n };
      return {
        ...prev,
        counts: { ...prev.counts, [line.id]: unitsFromBoxes(line, nextBoxes) ?? n },
        boxes: { ...(prev.boxes || {}), [line.id]: nextBoxes },
        startedAt: prev.startedAt || new Date().toISOString(),
      };
    });
  };

  const onLinked = (line, code, boxId) => {
    setUnknown(null);
    // the code now belongs to the product: every open line of it, on every order of the session, can take the next scan
    // and to the box it was linked to: a box still to measure opens with the code already filled in
    const onBox = (b) => (b.id === boxId && !b.vendor_barcode ? { ...b, vendor_barcode: code } : b);
    setOrder((o) =>
      o
        ? {
            ...o,
            barcodes: withLinkedCode(o.barcodes, o.lines, code, line.product_id, boxId),
            lines: boxId ? o.lines.map((l) => (l.product_id === line.product_id ? { ...l, boxes: l.boxes.map(onBox) } : l)) : o.lines,
          }
        : o
    );
    countLine(line, code, { line_id: line.id, kind: 'vendor', box_id: boxId || null }); // this carton goes where the operator pointed
    refetchOrder(); // the box as Odoo now has it
  };

  const openBoxSheet = (line, code = '', box = null, reopen = openLineId === line.id) => {
    setUnknown(null);
    setBoxSheet({ line, code, box, reopen });
    setOpenLineId(null);
  };

  /* A tap on a line whose product has no box on file goes to the box sheet
     first: measure and weigh the carton, then the line sheet opens for the
     count. Odoo refuses the receipt until the packaging is confirmed. */
  const tapLine = (line) => {
    if (line.status === 'open' && line.packaging_status === 'none') openBoxSheet(line, '', null, true);
    else setOpenLineId(line.id);
  };

  const refetchOrder = async () => {
    try {
      const fresh = combineOrders(await fetchOrders(poIds, api.order));
      setOrder(fresh);
      return fresh;
    } catch {
      return null;
    }
  };

  const onBoxSaved = async (box) => {
    const { line, code, box: edited, reopen } = boxSheet;
    setBoxSheet(null);
    const fresh = await refetchOrder();
    if (reopen) setOpenLineId(line.id);
    const scanned = (code || '').trim();
    const codes = [box.barcode, box.vendor_barcode].filter(Boolean);
    if (!edited && scanned && codes.includes(scanned)) {
      const freshLine = fresh?.lines.find((l) => l.id === line.id) || line;
      countLine(freshLine, scanned, { line_id: line.id, kind: 'vendor', box_id: box.id });
    }
  };

  const onBoxDeleted = async () => {
    const { line, reopen } = boxSheet;
    setBoxSheet(null);
    await refetchOrder();
    if (reopen) setOpenLineId(line.id);
  };

  const onFlags = async (line, flags) => {
    await api.setFlags(line.product_id, flags);
    await refetchOrder();
  };

  const onValidated = (ids) => {
    clearReceipt(ids); // validated: zero on every device of this login
    setProgress(loadCombinedProgress(poIds));
  };

  const onDone = (result, mode = 'list') => {
    setProgress(loadCombinedProgress(poIds)); // cleared by onValidated; a failed receipt keeps its counts
    setConfirming(false);
    setVerdict(null);
    if (mode === 'stay') {
      setOrder(null);
      load(true); // the backorders are now the open receipts: fresh lines, fresh destinations
      return;
    }
    navigate('/', { replace: true, state: { refresh: true } });
  };

  const multi = Boolean(order?.multi);

  // remember this receipt on the device, for "Recently opened" on the orders list
  useEffect(() => {
    if (order && order.ids?.length && poIds.every((p) => order.ids.includes(p))) {
      const entry = { ids: poIds, names: order.names || [order.name], supplier: order.supplier };
      rememberSession(entry);
      api.rememberReceipt(entry).catch(() => {}); // and for the other devices of this login
    }
  }, [order, poIds]);

  // a forgotten order on this truck: pick it, the counts already scanned stay
  const openAdd = async () => {
    const cached = loadOrdersCache()?.data?.orders;
    setAdding(cached || []);
    // the saved list may predate an order confirmed since: fetch a fresh one too
    api
      .orders()
      .then((d) => {
        setAdding((a) => (a ? d.orders : a));
        saveOrdersCache(d);
      })
      .catch(() => {});
  };

  return (
    <div className="min-h-screen pb-28">
      <ScanSink onScan={handleScan} active={scanning} />
      {flash > 0 && <div key={flash} className={cn('flash pointer-events-none fixed inset-0 z-50', verdict?.kind === 'over' ? 'bg-alert' : 'bg-out')} />}

      <TopBar
        title={order?.name || (poIds.length > 1 ? `${poIds.length} orders` : 'Order')}
        subtitle={order ? [order.supplier, order.expected && fmtDate(order.expected), !multi && order.pickings.join(', ')].filter(Boolean).join('\u00a0· ') : ' '}
        onBack={() => navigate('/')}
        right={
          <>
            <button type="button" onClick={openAdd} aria-label="Add an order to this receipt" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
              <Plus className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => navigate('/photos', { state: { po_ids: poIds } })} aria-label="Photograph the delivery note for these orders" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
              <FileText className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => load(true)} aria-label="Reload from Odoo" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
              <RefreshCw className="h-5 w-5" />
            </button>
          </>
        }
      >
        {order && (
          <div className="mx-auto max-w-lg px-3 pb-2">
            {multi && <p className="font-cond mb-1 truncate text-[15px] font-bold leading-tight text-ink" title={order.names.join(' · ')}>{order.names.map((n) => n.split(' ')[0]).join(' · ')}</p>}
            {/* The count carries the weight; everything else is one quiet line. */}
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 truncate text-[13px] text-ink2">
                <span className="font-cond text-[22px] font-bold leading-none text-ink">{qty(counted)}</span>
                <span className="font-cond text-[15px] font-semibold"> / {qty(expected)}</span> units
                {expectedCartons > expected && (
                  <>
                    {' '}· <span className="font-cond text-[15px] font-semibold text-ink">{qty(cartons)}</span>
                    <span className="font-cond text-[15px] font-semibold"> / {qty(expectedCartons)}</span> cartons
                  </>
                )}
              </p>
              <p className="flex shrink-0 items-center gap-1 text-[12px] text-ink2">
                {order.totals.open_lines} {order.totals.open_lines === 1 ? 'line' : 'lines'}
                {order.totals.shipped_lines > 0 && (
                  <>
                    {' '}· <Truck className="h-3.5 w-3.5" /> {order.totals.shipped_lines} shipped
                  </>
                )}
              </p>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line">
              <div className="h-full rounded-full bg-out transition-[width] duration-300" style={{ width: `${pct}%` }} />
            </div>
            {(order.totals.volume_l > 0 || order.size_tag || order.pallets > 0 || order.packages > 0 || order.totals.unsized_lines > 0 || order.totals.unconfirmed_lines > 0 || order.totals.fragile_lines > 0 || order.totals.pallet_lines > 0) && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink2 [@media(max-height:700px)]:hidden">
                {order.totals.volume_l > 0 && (
                  <span title="From confirmed packaging only">
                    {fmtVolume(order.totals.volume_l)}
                    {order.totals.weight_kg > 0 && ` · ${fmtWeight(order.totals.weight_kg)}`} to come
                  </span>
                )}
                <OrderTags order={{ size_tag: order.size_tag, size_tags: multi ? [] : order.size_tags, pallets: order.pallets, packages: order.packages, ...order.totals }} maxSize={4} />
              </div>
            )}
          </div>
        )}
      </TopBar>

      <main className="mx-auto max-w-lg px-3">
        {error && (
          <div className="mt-3 flex items-center justify-between rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">
            <span>{error}</span>
            <button type="button" onClick={() => load(true)} className="font-semibold">
              Retry
            </button>
          </div>
        )}
        {!order && !error && <Loader label={poIds.length > 1 ? 'Reading the orders from Odoo…' : 'Reading the order from Odoo…'} />}
        {order && (
          <>
            {dest === null && <p className="mt-2 px-1 text-[13px] text-ink2">Destinations unavailable right now. Counting still works.</p>}
            {openLines.length > 1 && (
              <label className="mt-2 flex h-11 items-center gap-2 rounded-xl border border-line bg-surface px-3 focus-within:border-ink">
                <Search className="h-4 w-4 shrink-0 text-ink2" />
                <input
                  value={find}
                  onChange={(e) => setFind(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                  placeholder={multi ? 'Find: code, EAN, name, PO' : 'Find: code, EAN or name'}
                  inputMode="search"
                  autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink2/90"
                />
                {finding ? (
                  <button type="button" onClick={() => setFind('')} aria-label="Clear" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-sunken">
                    <X className="h-4 w-4" />
                  </button>
                ) : (
                  <span className="text-[12px] text-ink2">or scan here</span>
                )}
              </label>
            )}
            {finding && (
              <p className="mt-1.5 px-1 text-[13px] text-ink2">
                {shownOpen.length + shownDone.length === 0
                  ? `No line matches “${find.trim()}”. A carton not on ${multi ? 'these orders' : 'this order'}: scan it with the field closed to look it up in Odoo.`
                  : `${shownOpen.length + shownDone.length} of ${order.lines.length} lines match`}
              </p>
            )}
            <ul className="mt-2 space-y-1.5">
              {visibleOpen.map((l) => (
                <li key={l.id} id={`line-${l.id}`}>
                  <LineRow
                    line={l}
                    showPo={multi}
                    count={progress.counts[l.id] || 0}
                    boxCounts={boxesOf(progress, l.id)}
                    dest={destFor(l.id)}
                    onOpen={() => tapLine(l)}
                  />
                </li>
              ))}
            </ul>
            {visibleOpen.length < shownOpen.length && (
              <button type="button" onClick={() => setLineLimit((n) => n + LINE_PAGE)} className="mt-2 h-12 w-full rounded-xl bg-surface text-[14px] font-semibold text-ink2 active:bg-sunken">
                Show {Math.min(LINE_PAGE, shownOpen.length - visibleOpen.length)} more of {shownOpen.length - visibleOpen.length}
              </button>
            )}
            {openLines.length === 0 && !finding && <p className="mt-8 text-center text-[15px] text-ink2">Nothing left to receive on {multi ? 'these orders' : 'this order'}.</p>}
            {doneLines.length > 0 && (
              <section className="mt-5">
                {finding ? (
                  shownDone.length > 0 && <p className="px-1 py-2 text-[13px] font-semibold text-ink2">Already received or without transfer ({shownDone.length})</p>
                ) : (
                  <button type="button" onClick={() => setShowDone((v) => !v)} className="flex w-full items-center justify-between px-1 py-2 text-[13px] font-semibold text-ink2">
                    <span>Already received or without transfer ({doneLines.length})</span>
                    <span>{showDone ? 'Hide' : 'Show'}</span>
                  </button>
                )}
                {shownDone.length > 0 && (
                  <ul className="space-y-1.5">
                    {shownDone.map((l) => (
                      <li key={l.id}>
                        <LineRow line={l} showPo={multi} count={0} dest={null} onOpen={() => setOpenLineId(l.id)} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        )}
      </main>

      {order && !modalOpen && (
        <div className="safe-b fixed inset-x-0 bottom-0 z-20 bg-bg/95 backdrop-blur">
          <div className="mx-auto flex max-w-lg gap-2 px-3 py-2">
            <button type="button" onClick={() => setCamera(true)} aria-label="Scan with the camera" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-surface active:bg-sunken">
              <Camera className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={counted === 0}
              className="flex h-14 flex-1 items-center justify-center gap-2 rounded-xl bg-ink text-[17px] font-semibold text-bg active:opacity-80 disabled:opacity-40"
            >
              <Check className="h-5 w-5" /> Confirm receipt{counted > 0 && ` (${qty(counted)})`}
            </button>
          </div>
        </div>
      )}

      {/* a scan that opened the line sheet for its packaging still says where the carton goes */}
      {(!modalOpen || (openLine && !(unknown || confirming || camera || boxSheet || choice || adding))) && <VerdictSheet verdict={verdict} onTop={modalOpen} onClose={() => setVerdict(null)} />}

      {openLine && (
        <QtySheet
          line={openLine}
          count={progress.counts[openLine.id] || 0}
          boxCounts={boxesOf(progress, openLine.id)}
          dest={destFor(openLine.id)}
          onChange={(n) => setUnits(openLine, n)}
          onBoxChange={(boxId, n) => setBox(openLine, boxId, n)}
          onAddBox={(l) => openBoxSheet(l)}
          onEditBox={(l, b) => openBoxSheet(l, '', b)}
          onFlags={onFlags}
          onClose={() => setOpenLineId(null)}
        />
      )}
      {unknown && (
        <UnknownSheet
          code={unknown.code}
          full={unknown.full}
          onCountAnyway={(line, entry) => {
            setUnknown(null);
            countLine(line, unknown.code, entry);
          }}
          order={order}
          onClose={() => setUnknown(null)}
          onJump={(o, mode = 'add') => {
            setUnknown(null);
            if (mode === 'alone') {
              navigate(`/orders/${o.id}`, { state: { scan: unknown.code } });
              return;
            }
            // keep the session: every order already open, plus this one; counts live per order on the device
            navigate(`/receive/${[...poIds.filter((p) => p !== o.id), o.id].join(',')}`, { state: { scan: unknown.code } });
          }}
          onLinked={onLinked}
          onAddBox={openBoxSheet}
          onCount={(line) => {
            setUnknown(null);
            tapLine(line);
          }}
        />
      )}
      {adding && (
        <PickOrdersSheet
          orders={adding}
          initial={poIds}
          title="Orders on this truck"
          subtitle="Tick the order you forgot. What you already counted stays."
          label={(n) => (n === 0 ? 'Pick the orders' : n === 1 ? 'Continue with this order' : `Continue with ${n} orders`)}
          onClose={() => setAdding(null)}
          onStart={(ids) => {
            setAdding(null);
            const next = [...poIds.filter((p) => ids.includes(p)), ...ids.filter((p) => !poIds.includes(p))];
            if (next.length && next.join(',') !== poIds.join(',')) navigate(`/receive/${next.join(',')}`);
          }}
        />
      )}
      {confirming && order && (
        <ConfirmSheet
          combined={order}
          counts={progress.counts}
          boxes={progress.boxes || {}}
          dest={dest}
          scans={progress.scans}
          onClose={() => setConfirming(false)}
          onDone={onDone}
          onValidated={onValidated}
          onFix={(line) => {
            // Straight to the line sheet, where the boxes get their size, barcode and the packaging its tick.
            setConfirming(false);
            setOpenLineId(line.id);
          }}
          onScanBox={(line) => {
            // the barcode is what's missing: straight to the box, field ready for the scanner
            setConfirming(false);
            if (line.boxes.length === 1) openBoxSheet(line, '', line.boxes[0], true);
            else setOpenLineId(line.id);
          }}
          onFlags={onFlags}
          onLeave={(line) => setUnits(line, 0)}
          onRefresh={refetchOrder}
        />
      )}
      {choice && (
        <ChooseLineSheet
          code={choice.code}
          choices={choice.choices}
          lastLineId={lastChoiceRef.current[choice.code] || null}
          showPo={multi}
          onPick={(c) => {
            lastChoiceRef.current[choice.code] = c.line.id;
            setChoice(null);
            countLine(c.line, choice.code, c.entry, { alsoOn: choice.choices.length - 1, chosen: true });
          }}
          onClose={() => setChoice(null)}
        />
      )}
      {boxSheet && (
        <BoxSheet
          line={boxSheet.line}
          box={boxSheet.box}
          prefillCode={boxSheet.code}
          onSaved={onBoxSaved}
          onDeleted={onBoxDeleted}
          onClose={() => {
            const { line, reopen } = boxSheet;
            setBoxSheet(null);
            if (reopen) setOpenLineId(line.id);
          }}
        />
      )}
      {camera && (
        <Suspense fallback={<div className="fixed inset-0 z-50 bg-black" />}>
          <CameraScanner onScan={handleScan} onClose={() => setCamera(false)} />
        </Suspense>
      )}
    </div>
  );
}
